/**
 * Real-time listener on supportTickets for the logged-in client.
 * Returns tickets with status in {open, acknowledged, investigating} + recently-resolved (last 7 days).
 */

import { useEffect, useState } from 'react';
import { collection, query, where, orderBy, onSnapshot } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { useAuth } from '../../hooks/useAuth';

export interface OpenTicket {
  id: string;
  clientId: string;
  status: 'open' | 'acknowledged' | 'investigating' | 'resolved';
  userComplaint: string;
  userFacingSummary: string;
  resolutionNote: string | null;
  createdAt?: { seconds: number };
  resolvedAt?: { seconds: number } | null;
}

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

export function useOpenTickets(): { tickets: OpenTicket[]; loading: boolean } {
  const { user } = useAuth();
  const [tickets, setTickets] = useState<OpenTicket[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setTickets([]);
      setLoading(false);
      return;
    }
    const q = query(
      collection(db, 'supportTickets'),
      where('clientId', '==', user.uid),
      orderBy('createdAt', 'desc')
    );
    const unsub = onSnapshot(q, snap => {
      const now = Date.now();
      const recent = snap.docs
        .map(d => ({ id: d.id, ...(d.data() as Omit<OpenTicket, 'id'>) }))
        .filter(t => {
          if (t.status !== 'resolved') return true;
          const resolvedMs = t.resolvedAt ? t.resolvedAt.seconds * 1000 : 0;
          return now - resolvedMs < SEVEN_DAYS_MS;
        });
      setTickets(recent);
      setLoading(false);
    });
    return unsub;
  }, [user]);

  return { tickets, loading };
}
